<script lang="ts" setup>
import projectStore from '@/store/ProjectStore'
import StepsControls from '@/components/steps/StepsControls.vue'
import { ModalResultType, ReportVariant } from '@/helper/Types'
import userStore from '@/store/UserStore'
import { validateComponent, ValidatorType } from '@/helper/ValidateDirective'
import FormElement from '@/components/FormElement.vue'
import { api } from '@/services/ApiService'
import ProjectController from '@/controllers/ProjectController'
import { ModalTypes } from '@/helper/ModalTypes'
import { hj, tagDownload, tagPageView } from '@/helper/analytics'
import { modalService } from '@/services/modals/ModalService'
import ModalParams from '@/services/modals/ModalParams'
import SectionHeadline from '@/components/SectionHeadline.vue'
import FuelTypeComparison from '@/components/FuelTypeSelector.vue'
import { computed, onMounted, onUnmounted, reactive, ref } from 'vue'
import i18n from '@/helper/Translations'
import { useRouter } from 'vue-router'
import { psIntegration } from '@/services/PsIntegrationService.ts'

const projectName = ref('')
const bomCheckbox = ref(false)
const validator: ValidatorType = reactive({})
const router = useRouter()
const shiftDown = ref(false)

onMounted(() => {
  setPageTitle()
  addKeyListeners()
  projectName.value = projectStore.projectProperties.name.val as string
  tagPageView('/download')
})

onUnmounted((): void => {
  document.removeEventListener('keydown', keyPressed)
  document.removeEventListener('keyup', keyPressed)
})

function bomChange() {
  projectStore.reportVariants = projectStore.reportVariants.filter(
    variant => ![ReportVariant.BILLOFMATERIAL_CSV, ReportVariant.BILLOFMATERIAL_XLSX].includes(variant)
  )

  if (bomCheckbox.value) {
    projectStore.reportVariants.push(ReportVariant.BILLOFMATERIAL_CSV, ReportVariant.BILLOFMATERIAL_XLSX)
  }
}

const isNextButtonDisabled = computed(() => {
  return (
    validator.electricityPrice?.error ||
    validator.comparisonFuelTypePellet?.error ||
    validator.comparisonFuelTypeOil?.error ||
    validator.comparisonFuelTypeGas?.error ||
    validator.comparisonFuelTypePricePellet?.error ||
    validator.comparisonFuelTypePriceOil?.error ||
    validator.comparisonFuelTypePriceGas?.error ||
    !projectStore.reportVariants.length
  )
})

function addKeyListeners() {
  document.addEventListener('keydown', keyPressed)
  document.addEventListener('keyup', keyPressed)
}

function setPageTitle(): void {
  document.title = i18n.global.t('modal.download_configuration_title')
}

async function clickDownload(e: MouseEvent): Promise<void> {
  if (bomCheckbox.value) {
    hj('bom_downloaded')
  }

  const variants = getReportVariants(e.ctrlKey || e.metaKey)
  if (e.shiftKey) {
    await api.openReportInBrowser(projectStore.projectId, variants)
  } else {

    await ProjectController.saveProject()
    await downloadClicked(variants)
    projectStore.reportVariants = [ReportVariant.CONSUMER, ReportVariant.INSTALLER]
  }
}

function getReportVariants(includeConsumptionCsv: boolean) {
  const variants = [...projectStore.reportVariants]
  if (includeConsumptionCsv) {
    console.debug('Electric consumption CSV requested')
    variants.push(ReportVariant.ELECTRIC_CONSUMPTION_CSV)
  }
  return variants.sort()
}

async function downloadClicked(variants: Array<ReportVariant>) {
  tagDownload()

  if (!userStore.enableSimulation && !userStore.planningSuiteToken) {
    return await api.downloadProject(projectStore.projectId, variants)
  }

  modalService.showModal(new ModalParams(ModalTypes.SIMULATION)).then()

  let simulationTimedOut = false

  const simulationTimeout = setTimeout(async () => {
    await simulationFinished(variants, false)
    simulationTimedOut = true
  }, 5 * 60 * 1000)

  const simulationSuccessful = !userStore.enableSimulation || await api.callProjectSimulation(projectStore.projectId)
  clearTimeout(simulationTimeout)
  !simulationTimedOut && (await simulationFinished(variants, simulationSuccessful))
}

async function simulationFinished(variants: Array<ReportVariant>, simulationSuccessful = true) {
  try {
    if (userStore.planningSuiteToken) {
      const result = simulationSuccessful && await psIntegration.planningSuiteResultData(projectStore.projectId, variants)

      modalService
        .showModal(new ModalParams(result ? ModalTypes.PS_UPLOAD_SUCCESS : ModalTypes.PS_UPLOAD_FAILED))
        .then(modalResult => {
          if (modalResult.resultType === ModalResultType.CONFIRM || modalResult.resultType === ModalResultType.CLOSE) {
            psIntegration.closeIntegration()
          }
        })
    } else {
      await api.downloadProject(projectStore.projectId, variants)

      modalService
        .showModal(new ModalParams(simulationSuccessful ? ModalTypes.SIMULATION_SUCCESS : ModalTypes.SIMULATION_ERROR))
        .then(modalResult => {
          if (modalResult.resultType === ModalResultType.CONFIRM) {
            router.push({ name: 'project', params: { projectId: 'new' } })
          } else {
            router.push({ name: 'ProjectOverviewPage' })
          }
        })
    }
  } catch (e) {
    if (userStore.planningSuiteToken) {
      await modalService
        .showModal(new ModalParams(ModalTypes.PS_UPLOAD_FAILED))
        .then(modalResult => {
          if (modalResult.resultType === ModalResultType.CONFIRM || modalResult.resultType === ModalResultType.CLOSE) {
            psIntegration.closeIntegration()
          }
        })
    } else {
      await modalService.showModal(new ModalParams(ModalTypes.DOWNLOAD_SIMULATION_FAILED))
    }
    throw Error((e as Error).message)
  }
}

function keyPressed(e: KeyboardEvent): void {
  shiftDown.value = e.shiftKey
}

async function clickBack(): Promise<void> {
  const { fromRoute } = window.history.state

  if (fromRoute) {
    await router.push({ path: fromRoute as string })
  } else {
    await router.push({ path: `/project/${projectStore.projectId}` })
  }
}

const validate = () => !isNextButtonDisabled.value && validateComponent(validator)

const showReportPricesCustomer = computed(() => {
  return projectStore.reportVariants.includes(ReportVariant.CONSUMER)
})

const showReportPricesInstaller = computed(() => {
  return projectStore.reportVariants.includes(ReportVariant.INSTALLER)
})

defineExpose({
  validate
})
</script>

<template>
  <header class="header">
    <div class="container header__container">
      <h1 class="header__title">
        <span>{{ projectName }} - {{ $t('modal.download_configuration_title') }}</span>
      </h1>
    </div>
  </header>

  <div class="main-wrapper" data-hj-whitelist>
    <div class="container main">
      <div class="card form">
        <section class="report-type-select">
          <SectionHeadline translation-key="modal.download_configuration_report_type_title" />
          <p>{{ $t('modal.report_type_text') }}</p>

          <FormElement>
            <div class="fTable is--products-table has--selects is--add-product-table">
              <div class="fTable__item is--select">
                <div>
                  <label class="form__checkbox">
                    <input
                      :id="ReportVariant.CONSUMER"
                      v-model="projectStore.reportVariants"
                      :value="ReportVariant.CONSUMER"
                      type="checkbox"
                    >
                    <span class="form__cm" />
                  </label>
                  <label :for="ReportVariant.CONSUMER" class="fTable__tr fTable__cb">
                    <div class="fTable__td column is--product">
                      <div class="fTable__product-infos">
                        <p class="report-type-select__headline">
                          {{ $t('modal.report_type_label_consumer') }}
                        </p>
                        <p
                          v-sanitize-html="$t('modal.report_type_consumer_text')"
                          class="report-type-select__text"
                        />
                      </div>
                    </div>
                  </label>
                </div>
              </div>
              <div class="fTable__item is--select">
                <label class="form__checkbox">
                  <input
                    :id="ReportVariant.INSTALLER"
                    v-model="projectStore.reportVariants"
                    :value="ReportVariant.INSTALLER"
                    type="checkbox"
                  >
                  <span class="form__cm" />
                </label>
                <label :for="ReportVariant.INSTALLER" class="fTable__tr fTable__cb">
                  <div class="fTable__td column is--product">
                    <div class="fTable__product-infos">
                      <p class="report-type-select__headline">
                        {{ $t('modal.report_type_label_installer') }}
                      </p>
                      <p v-sanitize-html="$t('modal.report_type_installer_text')" class="report-type-select__text" />
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </FormElement>
        </section>

        <section class="report-options">
          <SectionHeadline translation-key="modal.download_configuration_report_options_title" />

          <FormElement>
            <div class="fTable is--products-table has--selects is--add-product-table">
              <div
                v-if="userStore.enableShowPricesInReportOption && !userStore.hidePrices && showReportPricesCustomer"
                class="fTable__item is--select">
                <label class="form__checkbox">
                  <input id="show-prices-customer" v-model="projectStore.showPricesCustomer" type="checkbox">
                  <span class="form__cm" />
                </label>
                <label class="fTable__tr fTable__cb" for="show-prices-customer">
                  <div class="fTable__td column is--product">
                    <div class="fTable__product-infos">
                      <p class="report-type-select__headline">
                        {{ $t('modal.enable_show_prices_customer') }}
                      </p>
                      <p v-sanitize-html="$t('modal.show_prices_text')" class="report-type-select__text" />
                    </div>
                  </div>
                </label>
              </div>

              <div
                v-if="userStore.enableShowPricesInReportOption && !userStore.hidePrices && showReportPricesInstaller"
                class="fTable__item is--select">
                <label class="form__checkbox">
                  <input id="show-prices" v-model="projectStore.showPrices" type="checkbox">
                  <span class="form__cm" />
                </label>
                <label class="fTable__tr fTable__cb" for="show-prices">
                  <div class="fTable__td column is--product">
                    <div class="fTable__product-infos">
                      <p class="report-type-select__headline">
                        {{ $t('modal.enable_show_prices_installer') }}
                      </p>
                      <p v-sanitize-html="$t('modal.show_prices_text')" class="report-type-select__text" />
                    </div>
                  </div>
                </label>
              </div>

              <div class="fTable__item is--select">
                <label class="form__checkbox">
                  <input id="include-billofmaterials" v-model="bomCheckbox" type="checkbox" @change="bomChange">
                  <span class="form__cm" />
                </label>
                <label class="fTable__tr fTable__cb" for="include-billofmaterials">
                  <div class="fTable__td column is--product">
                    <div class="fTable__product-infos">
                      <p class="report-type-select__headline">
                        {{ $t('modal.enable_include_billofmaterial') }}
                      </p>
                      <p v-sanitize-html="$t('modal.include_billofmaterial_text')" class="report-type-select__text" />
                    </div>
                  </div>
                </label>
              </div>

              <div v-if="userStore.enableExtendedNoiseCalculation" class="fTable__item is--select">
                <label class="form__checkbox">
                  <input
                    id="show-extended-noise-values"
                    v-model="projectStore.showExtendedNoiseValues"
                    type="checkbox"
                  >
                  <span class="form__cm" />
                </label>
                <label class="fTable__tr fTable__cb" for="show-extended-noise-values">
                  <div class="fTable__td column is--product">
                    <div class="fTable__product-infos">
                      <p class="report-type-select__headline">
                        {{ $t('modal.show_extended_noise_values') }}
                      </p>
                      <p v-sanitize-html="$t('modal.show_extended_noise_values_text')" class="report-type-select__text" />
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </FormElement>
        </section>

        <section class="energy-prices">
          <SectionHeadline translation-key="modal.download_configuration_energy_price_title" />
          <FuelTypeComparison :validator="validator" />
        </section>
      </div>
    </div>
  </div>

  <StepsControls>
    <template #back-button>
      <button class="btn is--secondary controls__back" @click="clickBack()">
        {{ $t('modal.download_configuration_btn_cancel') }}
      </button>
    </template>
    <template #next-button>
      <button v-if="!userStore.planningSuiteToken" :disabled="!!isNextButtonDisabled" class="btn controls__next" @click="clickDownload">
        <i class="ic-arrow-to-bottom hide-mobile" />
        {{ userStore.isPrivileged && shiftDown ? 'Open Report in new tab' : $t('global.btn_export') }}
      </button>
      <button v-else :disabled="!!isNextButtonDisabled" class="btn controls__next" @click="clickDownload">
        <i class="ic-submit hide-mobile" />
        {{ $t('planning_suite.btn_upload') }}
      </button>
    </template>
  </StepsControls>
</template>

<style lang="scss" scoped>
@import '@/assets/style/helpers/include-media';

.card {
  overflow: visible;
}

.header__container {
  justify-content: center;
}

.header__title {
  height: auto;
  margin-bottom: 0;
  color: #fff;
  line-height: 1.5rem;
  text-align: center;
  margin-right: 0;
  @include media('<tablet') {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    font-size: rem-calc(18);
    span {
      font-size: rem-calc(18);
      line-height: 1;
    }
  }

}

section {
  padding: rem-calc(32);

  @include media('<tablet') {
    padding: 1rem;
  }

  &:not(:last-child) {
    border-bottom: 1px solid $borderColorLight;
  }

  .form__element {
    margin-bottom: 0;
  }
}

.fTable.is--products-table .fTable__item {
  border-bottom: 0;
}

.report-type-select,
.report-options {
  .fTable {
    margin-top: rem-calc(12);
    width: 100%;

    &.is--products-table.has--selects .fTable__item {
      border-bottom: 0;
    }

    @include themify($themes) {
      border-radius: rem-calc(themed('cardBorderRadius'));
    }

    .form__checkbox {
      display: flex;
      height: 100%;
      position: absolute;
      width: rem-calc(64);
      justify-content: center;

      @include media('<tablet') {
        width: 2rem;
      }

      .form__cm {
        position: static;
        margin: 0;
      }
    }

    .column {
      padding-left: rem-calc(64) !important;

      @include media('<tablet') {
        padding: 1rem 0 1rem 3rem !important;
      }
    }

    .report-type-select__headline {
      font-size: rem-calc(18);
      line-height: rem-calc(28);
      margin: 0;
      flex: 1;
    }

    .report-type-select__text {
      font-size: rem-calc(14);
      line-height: rem-calc(17);
    }
  }

}

.simulation-template-ids {
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  flex-wrap: wrap;

  .form__element {
    flex: 1;
  }
}
</style>
